import { Input, Textarea } from '@ember/component';
import { fn, get, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { guidFor } from '@ember/object/internals';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import willDestroy from '@ember/render-modifiers/modifiers/will-destroy';
import { service } from '@ember/service';
import { dasherize } from '@ember/string';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { eq } from 'ember-truth-helpers';
import { trackedFunction } from 'reactiveweb/function';

import { getBem } from 'umg-ember-backend/utils/get-bem';
import { getRecordSettings } from 'umg-ember-backend/utils/get-record-settings';
import { getRecordType } from 'umg-ember-backend/utils/get-record-type';
import { translate } from 'umg-ember-backend/utils/intl';

import BelongsTo from './belongs-to';
import Checkbox from './checkbox';
import CodeEditor from './code-editor';
import Color from './color';
import Datetime from './datetime';
import HasMany from './has-many';
import HasManyJoin from './has-many-join';
import SelectMultiple from './select-multiple';
import SelectRecord from './select-record';
import SelectValue from './select-value';
import StringArray from './string-array';
import styles from './styles.module.css';
import TextEditor from './text-editor';

const bem = getBem(styles);

export default class UiFormField extends Component {
  // Services
  @service abilities;
  @service intl;
  @service permission;
  @service recordLocks;
  @service recordStatus;
  @service recordVariants;
  @service store;

  // Defaults
  @tracked isValidationVisible = false;

  // Getter and setter
  get fieldId() {
    const key = guidFor(this.args.model ?? this);
    const property = this.args.property ?? '';

    return `${this.args.recordType}-${key}-${dasherize(property)}`;
  }

  get hintString() {
    const lookups = [
      `forms.${this.args.recordType}.hints.${this.args.property}`,
      `forms.defaults.hints.${this.args.property}`,
    ];

    return translate.call(this, this.args.hint, lookups, {
      silent: true,
      intlOptions: { htmlSafe: true },
    });
  }

  get isLocked() {
    return this.recordLocks.isLocked(this.args.model);
  }

  get isDisabled() {
    return (
      this.args.disabled ||
      this.isLocked ||
      this.recordStatus.isBlocked(this.args.model) ||
      !this.args.hasPermissionToEdit
    );
  }

  get isFieldVisible() {
    const isVariant = this.recordVariants.isVariant(this.args.model);
    const variantProperties =
      getRecordSettings(this.args.model, 'variantProperties') ?? [];
    const isVariantProperty = variantProperties.includes(this.args.property);

    // If model is a variant and property is not visible in variant
    if (isVariant && !isVariantProperty) {
      return false;
    }

    const modelPermissions = this.permission.for(
      getRecordType(this.args.model),
    );

    // Check if user has permission to edit property
    return !modelPermissions?.excludedProperties?.includes(this.args.property);
  }

  get isRequired() {
    return (
      this.validation?.options?.presence?.presence &&
      !this.validation?.options?.presence?.disabled
    );
  }

  get labelString() {
    const lookups = [
      `forms.${this.args.recordType}.fields.${this.args.property}`,
      `forms.defaults.fields.${this.args.property}`,
    ];

    return translate.call(this, this.args.label, lookups);
  }

  get placeholderString() {
    const lookups = [
      `forms.${this.args.recordType}.placeholders.${this.args.property}`,
      `forms.defaults.placeholders.${this.args.property}`,
    ];

    return translate.call(this, this.args.placeholder, lookups, {
      silent: true,
    });
  }

  get relationshipMeta() {
    const { recordType, property } = this.args;

    // For some forms, we use fake models (embed-code, tool-itunes-import, ...)
    // whiche can't be found in the schema.
    if (!this.store.schema.hasResource({ type: recordType })) {
      return null;
    }

    const fields = this.store.schema.fields({
      type: recordType,
    });
    const field = fields.get(property);

    if (['belongsTo', 'hasMany'].includes(field?.kind)) {
      return field;
    }

    return null;
  }

  resolvedValue = trackedFunction(this, async () => {
    return await this.args.model[this.args.property];
  });

  get value() {
    if (this.relationshipMeta) {
      let defaultValue = this.relationshipMeta.kind === 'belongsTo' ? null : [];
      return this.resolvedValue.value || defaultValue;
    }

    if (this.args.model.get) {
      return this.args.model.get(this.args.property);
    }

    return this.args.model[this.args.property];
  }

  set value(value) {
    if (this.args.model.set) {
      this.args.model.set(this.args.property, value);
    } else {
      this.args.model[this.args.property] = value;
    }
  }

  get showErrors() {
    // Show errors if formShowValidationErrors is set
    if (this.args.formShowValidationErrors && this.validation?.isInvalid) {
      return true;
    }

    // Show errors on existing records
    let model = this.args.model;
    let isNew = model.get ? model.get('isNew') : model.isNew;

    if (!isNew && this.validation?.isInvalid) {
      return true;
    }

    return this.isValidationVisible && this.validation?.isInvalid;
  }

  get showLabel() {
    return this.args.label !== false;
  }

  get type() {
    return this.args.type ?? 'text';
  }

  get validation() {
    if (this.args.model.get) {
      return this.args.model.get(`validations.attrs.${this.args.property}`);
    }

    return this.args.model?.validations?.attrs[this.args.property];
  }

  // Functions
  showValidations = () => {
    this.isValidationVisible = true;
  };

  registerFieldToFieldset = () => {
    if (this.args.registerField) {
      this.args.registerField(this.args.property);
    }
  };

  unregisterFieldToFieldset = () => {
    if (this.args.unregisterField) {
      this.args.unregisterField(this.args.property);
    }
  };

  // Template
  <template>
    {{#if this.isFieldVisible}}
      <div
        class={{bem
          (hash is-required=this.isRequired has-error=this.showErrors)
        }}
        data-test-ui-form-field
        data-test-ui-form-field-property={{@property}}
        data-test-ui-form-field-type={{this.type}}
        {{didInsert this.registerFieldToFieldset}}
        {{willDestroy this.unregisterFieldToFieldset}}
      >
        {{#if this.showLabel}}
          <label for={{this.fieldId}} class={{bem 'label'}}>
            {{this.labelString}}
          </label>
        {{/if}}

        {{#if (eq this.type 'belongs-to')}}
          <BelongsTo
            @filters={{@filters}}
            @fieldId={{this.fieldId}}
            @isDisabled={{this.isDisabled}}
            @labelProperty={{@belongsToLabel}}
            @model={{@model}}
            @recordType={{@recordType}}
            @on={{@on}}
            @options={{@options}}
            @property={{@property}}
            @value={{this.value}}
            ...attributes
            as |record labelProperty|
          >
            {{#if (has-block)}}
              {{yield record}}
            {{else}}
              {{get record labelProperty}}
            {{/if}}
          </BelongsTo>
        {{else if (eq this.type 'checkbox')}}
          <Checkbox
            @fieldId={{this.fieldId}}
            @isChecked={{readonly this.value}}
            @isDisabled={{this.isDisabled}}
            @model={{@model}}
            @recordType={{@recordType}}
            @onChange={{@checkboxAction}}
            @property={{@property}}
          />
        {{else if (eq this.type 'code-editor')}}
          <CodeEditor
            @isDisabled={{this.isDisabled}}
            @mode={{@codeFormat}}
            @onUpdate={{fn (mut this.value)}}
            @value={{this.value}}
          />
        {{else if (eq this.type 'color')}}
          <Color
            @format={{@colorFormat}}
            @isDisabled={{this.isDisabled}}
            @onChange={{fn (mut this.value)}}
            @required={{this.isRequired}}
            @value={{this.value}}
          />
        {{else if (eq this.type 'has-many')}}
          <HasMany
            @fieldId={{this.fieldId}}
            @filters={{@filters}}
            @insertAtEnd={{@insertAtEnd}}
            @isDisabled={{this.isDisabled}}
            @model={{@model}}
            @recordType={{@recordType}}
            @on={{@on}}
            @options={{@options}}
            @property={{@property}}
            @reorderItems={{@hasManyReorderItems}}
            @required={{this.isRequired}}
            @showErrors={{@showErrors}}
            @sortable={{@hasManySortable}}
            @sortableColumn={{@hasManySortableColumn}}
            @value={{this.value}}
            as |record labelProperty index|
          >
            {{#if (has-block)}}
              {{yield record this index}}
            {{else}}
              {{get record labelProperty}}
            {{/if}}
          </HasMany>
        {{else if (eq this.type 'has-many-join')}}
          <HasManyJoin
            @fieldId={{this.fieldId}}
            @filters={{@filters}}
            @foreignModelName={{@hasManyForeignModelName}}
            @insertAtEnd={{@insertAtEnd}}
            @isDisabled={{this.isDisabled}}
            @model={{@model}}
            @recordType={{@recordType}}
            @on={{@on}}
            @options={{@options}}
            @property={{@property}}
            @reorderItems={{@hasManyReorderItems}}
            @required={{this.isRequired}}
            @showErrors={{@showErrors}}
            @sortable={{@hasManySortable}}
            @sortableColumn={{@hasManySortableColumn}}
            @value={{this.value}}
            as |record labelProperty index|
          >
            {{#if (has-block)}}
              {{yield record this index}}
            {{else}}
              {{get record labelProperty}}
            {{/if}}
          </HasManyJoin>
        {{else if (eq this.type 'select-record')}}
          <SelectRecord
            @isDisabled={{this.isDisabled}}
            @isRequired={{this.isRequired}}
            @isSearchEnabled={{@isSearchEnabled}}
            @labelProperty={{@labelProperty}}
            @model={{@model}}
            @onChange={{@onChange}}
            @onSearch={{@onSearch}}
            @onShowValidations={{this.showValidations}}
            @options={{@options}}
            @placeholder={{this.placeholderString}}
            @property={{@property}}
            @renderInPlace={{@renderInPlace}}
            @searchField={{@searchField}}
            @selectPath={{@selectRecordPath}}
            @value={{this.value}}
            as |option labelProperty|
          >
            {{#if (has-block)}}
              {{yield option this}}
            {{else}}
              {{get option labelProperty}}
            {{/if}}
          </SelectRecord>
        {{else if (eq this.type 'select-multiple')}}
          <SelectMultiple
            @isDisabled={{this.isDisabled}}
            @isRequired={{this.isRequired}}
            @isSearchEnabled={{@isSearchEnabled}}
            @labelProperty={{@labelProperty}}
            @model={{@model}}
            @onChange={{@onChange}}
            @onSearch={{@onSearch}}
            @onShowValidations={{this.showValidations}}
            @options={{@options}}
            @placeholder={{this.placeholderString}}
            @property={{@property}}
            @renderInPlace={{@renderInPlace}}
            @searchField={{@searchField}}
            @selected={{@selected}}
            @selectPath={{@selectPath}}
            @value={{this.value}}
            as |option labelProperty|
          >
            {{#if (has-block)}}
              {{yield option this}}
            {{else}}
              {{get option labelProperty}}
            {{/if}}
          </SelectMultiple>
        {{else if (eq this.type 'select-value')}}
          <SelectValue
            @isDisabled={{this.isDisabled}}
            @isRequired={{this.isRequired}}
            @isSearchEnabled={{@isSearchEnabled}}
            @labelProperty={{@labelProperty}}
            @model={{@model}}
            @onChange={{@onChange}}
            @onSearch={{@onSearch}}
            @onShowValidations={{this.showValidations}}
            @options={{@options}}
            @placeholder={{this.placeholderString}}
            @property={{@property}}
            @renderInPlace={{@renderInPlace}}
            @searchField={{@searchField}}
            @selectPath={{@selectPath}}
            @selected={{@selected}}
            @value={{this.value}}
            as |option labelProperty|
          >
            {{#if (has-block)}}
              {{yield option this}}
            {{else}}
              {{get option labelProperty}}
            {{/if}}
          </SelectValue>
        {{else if (eq this.type 'text-editor')}}
          <TextEditor
            @disabled={{this.isDisabled}}
            @fieldId={{this.fieldId}}
            @height={{@textEditorHeight}}
            @model={{@model}}
            @onChange={{fn (mut this.value)}}
            @preset={{@textEditorPreset}}
            @required={{this.isRequired}}
            @value={{this.value}}
          />
        {{else if (eq this.type 'textarea')}}
          <Textarea
            class={{bem 'input'}}
            disabled={{this.isDisabled}}
            id={{this.fieldId}}
            name={{this.fieldId}}
            placeholder={{this.placeholderString}}
            required={{this.isRequired}}
            rows={{8}}
            @value={{this.value}}
            {{on 'focusout' this.showValidations}}
          />
        {{else if (eq this.type 'date')}}
          <Datetime
            @displayFormat='d.m.Y'
            @enableTime={{false}}
            @icon='calendar'
            @inputFormat='Y-m-d'
            @isDisabled={{this.isDisabled}}
            @isRequired={{this.isRequired}}
            @onChange={{fn (mut this.value)}}
            @placeholder={{this.placeholderString}}
            @value={{this.value}}
          />
        {{else if (eq this.type 'date-time')}}
          <Datetime
            @displayFormat='d.m.Y H:i'
            @enableTime={{true}}
            @icon='calendar'
            @inputFormat='Y-m-d H:i'
            @isDisabled={{this.isDisabled}}
            @isRequired={{this.isRequired}}
            @onChange={{fn (mut this.value)}}
            @placeholder={{this.placeholderString}}
            @value={{this.value}}
          />
        {{else if (eq this.type 'time')}}
          <Datetime
            @displayFormat='H:i'
            @enableTime={{true}}
            @icon='time'
            @inputFormat='H:i'
            @isDisabled={{this.isDisabled}}
            @isRequired={{this.isRequired}}
            @noCalendar={{true}}
            @onChange={{fn (mut this.value)}}
            @placeholder={{this.placeholderString}}
            @value={{this.value}}
          />
        {{else if (eq this.type 'string-array')}}
          <StringArray
            @isDisabled={{this.isDisabled}}
            @isRequired={{this.isRequired}}
            @onChange={{fn (mut this.value)}}
            @placeholder={{this.placeholderString}}
            @value={{this.value}}
          />
        {{else}}
          {{#if (has-block)}}
            {{yield
              (hash
                fieldId=this.fieldId
                disabled=this.isDisabled
                hasPermissionToEdit=this.hasPermissionToEdit
                recordType=@recordType
                placeholder=this.placeholderString
                property=@property
                required=this.isRequired
                focusOut=this.showValidations
              )
              this.value
            }}
          {{else}}
            <Input
              disabled={{this.isDisabled}}
              id={{this.fieldId}}
              name={{this.fieldId}}
              class={{bem 'input'}}
              placeholder={{this.placeholderString}}
              required={{this.isRequired}}
              aria-label={{this.labelString}}
              @type={{this.type}}
              @value={{this.value}}
              {{on 'focusout' this.showValidations}}
              ...attributes
            />
          {{/if}}
        {{/if}}

        {{#if this.hintString}}
          <div class={{bem 'hint'}}>{{this.hintString}}</div>
        {{/if}}

        {{#if this.showErrors}}
          <div class={{bem 'error'}}>
            {{this.validation.message}}
          </div>
        {{/if}}
      </div>
    {{/if}}
  </template>
}
